import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import type { BaseDataLayerKey } from "fond/project/panels/ExtendedDesign/BaseData/layers";
import type { BaseDataTaskSource, TaskStage } from "fond/project/panels/ExtendedDesign/BaseData/taskPhases";
import { ProjectAction } from "fond/project/reduxActions";
import { Store } from "fond/types";

/** A line of a task's result, e.g. "73 addresses added", under an optional title naming its layer. */
export type TaskResultLine = { title?: string; message: string };

/** A task started from a base data row's flyout. */
export type BaseDataTaskRecord = {
  source: BaseDataTaskSource;
  /** Base data rows the task writes to. */
  keys: BaseDataLayerKey[];
  /** The stage the task is on; reading a polygon file is followed through polygon select. */
  stage: TaskStage;
  /** Upload slot sending the task's data, while it uploads. */
  uploadId?: string;
  status: "running" | "succeeded" | "failed";
  result?: TaskResultLine[];
  /** Why the task failed, when known. */
  error?: string;
};

/**
 * The latest task of each base data row, keyed by the row whose flyout started it.
 *
 * A record lasts until another task starts from the same row, it is dismissed, or the version changes.
 */
export type BaseDataTaskRecords = Partial<Record<BaseDataLayerKey, BaseDataTaskRecord>>;

export type BaseDataTasksState = {
  /** The version the tasks belong to. */
  versionId: string | null;
  records: BaseDataTaskRecords;
};

type TaskStart = Pick<BaseDataTaskRecord, "source" | "keys" | "stage" | "uploadId">;

type TaskAdvance = { key: BaseDataLayerKey } & Pick<BaseDataTaskRecord, "stage" | "uploadId">;

const initialState: BaseDataTasksState = { versionId: null, records: {} };

/** Return the state with a row's task updated, if it is running. */
const updateRunning = (
  state: BaseDataTasksState,
  key: BaseDataLayerKey,
  update: (task: BaseDataTaskRecord) => BaseDataTaskRecord
): BaseDataTasksState => {
  const task = state.records[key];
  return task?.status === "running" ? { ...state, records: { ...state.records, [key]: update(task) } } : state;
};

/**
 * Redux slice storing the tasks started from the Input Data panel, so its rows and flyouts show the same progress and outcome.
 */
export const baseDataTasksSlice = createSlice({
  name: "baseDataTasks",
  initialState,
  reducers: {
    startBaseDataTask: (state, { payload: { key, ...task } }: PayloadAction<TaskStart & { key: BaseDataLayerKey }>): BaseDataTasksState => ({
      ...state,
      records: { ...state.records, [key]: { ...task, status: "running" } },
    }),
    advanceBaseDataTask: (state, { payload: { key, ...stage } }: PayloadAction<TaskAdvance>): BaseDataTasksState =>
      updateRunning(state, key, (task) => ({ ...task, ...stage })),
    succeedBaseDataTask: (state, { payload: { key, result } }: PayloadAction<{ key: BaseDataLayerKey; result?: TaskResultLine[] }>) =>
      updateRunning(state, key, (task) => ({ ...task, status: "succeeded", result })),
    failBaseDataTask: (state, { payload: { key, error } }: PayloadAction<{ key: BaseDataLayerKey; error?: string }>) =>
      updateRunning(state, key, (task) => ({ ...task, status: "failed", error })),
    dismissBaseDataTask: (state, { payload: key }: PayloadAction<BaseDataLayerKey>): BaseDataTasksState => ({
      ...state,
      records: Object.fromEntries(Object.entries(state.records).filter(([taskKey]) => taskKey !== key)),
    }),
  },
  extraReducers: (builder) => {
    // Clears the tasks when another version is selected; SET_VERSION is also sent for the current version.
    builder.addCase(
      ProjectAction.SET_VERSION,
      (state, action: { type: string; versionId: string }): BaseDataTasksState =>
        action.versionId === state.versionId ? state : { versionId: action.versionId, records: {} }
    );
  },
});

export const { startBaseDataTask, advanceBaseDataTask, succeedBaseDataTask, failBaseDataTask, dismissBaseDataTask } = baseDataTasksSlice.actions;

/** The latest task started from each base data row. */
export const selectBaseDataTaskRecords = (state: Store): BaseDataTaskRecords => state.baseDataTasks.records;
